import React from 'react';
import { Bug, Lock, Activity } from 'lucide-react';
import DistributionPieChart from './DistributionPieChart';
import { ChartCard } from '@/components/ui/card';

interface DistributionData {
  name: string;
  count: number;
  color: string;
}

interface VulnerabilityDistributionProps {
  data: DistributionData[];
  height?: number;
  className?: string;
  onNavigate?: (route: string, params?: any) => void;
  applicationId?: number;
  projectId?: number;
}

export const VulnerabilityDistribution: React.FC<VulnerabilityDistributionProps> = ({
  data,
  height = 192,
  className = '',
  onNavigate,
  applicationId,
  projectId
}) => {

  const handleSliceClick = (sliceName: string, count: number) => {
    if (!onNavigate || count === 0) return;

    const severityMap: { [key: string]: string } = {
      'Critical': 'critical',
      'High': 'high',
      'Medium': 'medium',
      'Low': 'low'
    };

    const riskLevel = severityMap[sliceName];
    if (!riskLevel) return;

    const queryParams = new URLSearchParams();
    if (applicationId) queryParams.set('applicationId', applicationId.toString());
    if (projectId) queryParams.set('projectId', projectId.toString());

    queryParams.set('filter', 'vulnerabilityscore:' + riskLevel);
    queryParams.set('columns', 'vulnerabilityscore');
    onNavigate(`components-hub?${queryParams.toString()}`);
  };
  return (
    <ChartCard
      title="Vulnerability Distribution"
      description="Risk Distribution of components across all the Applications"
      hint="Breakdown of all components and dependencies by vulnerability severity across applications."
      icon={Bug}
      color="red"
      children={() => (
        <DistributionPieChart
          data={data}
          height={height}
          className={className}
          emptyStateIcon={Bug}
          emptyStateMessage="No vulnerability data available"
          onSliceClick={handleSliceClick}
        />
      )}
      legends={() => (
        <>
          {data.map((item) => (
            <div key={item.name} className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm">{item.name}: {item.count}</span>
            </div>
          ))}
        </>
      )}
    />
  );
};

interface LicenseRiskDistributionProps {
  data: DistributionData[];
  height?: number;
  className?: string;
  onNavigate?: (route: string, params?: any) => void;
  applicationId?: number;
  projectId?: number;
}

export const LicenseRiskDistribution: React.FC<LicenseRiskDistributionProps> = ({
  data,
  height = 192,
  className = '',
  onNavigate,
  applicationId,
  projectId
}) => {

  const handleSliceClick = (sliceName: string, count: number) => {
    if (!onNavigate || count === 0) return;

    const riskLevelMap: { [key: string]: string } = {
      'Critical': 'critical',
      'High': 'high',
      'Medium': 'medium',
      'Low': 'low'
    };

    const riskLevel = riskLevelMap[sliceName];
    if (!riskLevel) return;

    const queryParams = new URLSearchParams();
    if (applicationId) queryParams.set('applicationId', applicationId.toString());
    if (projectId) queryParams.set('projectId', projectId.toString());
    queryParams.set('filter', 'licensescore:' + riskLevel);
    queryParams.set('columns', 'licensescore');
    onNavigate(`components-hub?${queryParams.toString()}`);
  };
  return (
    <ChartCard
      title="License Risk Distribution"
      description="Risk Distribution of components across all the Applications"
      hint="Breakdown of all components and dependencies by license risk severity across applications."

      icon={Lock}
      color="orange"
      children={() => (
        <DistributionPieChart
          data={data}
          height={height}
          className={className}
          emptyStateIcon={Lock}
          emptyStateMessage="No license risk data available"
          onSliceClick={handleSliceClick}
        />
      )}
      legends={() => (
        <>
          {data.map((item) => (
            <div key={item.name} className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm">{item.name}: {item.count}</span>
            </div>
          ))}
        </>
      )}
    />
  );
};

interface OperationalRiskDistributionProps {
  data: DistributionData[];
  height?: number;
  className?: string;
  onNavigate?: (route: string, params?: any) => void;
  applicationId?: number;
  projectId?: number;
}

export const OperationalRiskDistribution: React.FC<OperationalRiskDistributionProps> = ({
  data,
  height = 192,
  className = '',
  onNavigate,
  applicationId,
  projectId
}) => {

  const handleSliceClick = (sliceName: string, count: number) => {
    if (!onNavigate || count === 0) return;

    const riskLevelMap: { [key: string]: string } = {
      'Critical': 'critical',
      'High': 'high',
      'Medium': 'medium',
      'Low': 'low'
    };

    const riskLevel = riskLevelMap[sliceName];
    const queryParams = new URLSearchParams();
    if (applicationId) queryParams.set('applicationId', applicationId.toString());
    if (projectId) queryParams.set('projectId', projectId.toString());
    queryParams.set('filter', 'operationalriskscore:' + riskLevel);
    queryParams.set('columns', 'operationalriskscore');
    onNavigate(`components-hub?${queryParams.toString()}`);
  };
  return (
    <ChartCard
      title="Operational Risk Distribution"
      description="Risk Distribution of components across all the Applications"
      hint="Reflects the presence of deprecated, unmaintained, or outdated components. Considers last update dates and maintainer deprecation or archival declarations."
      icon={Activity}
      color="purple"
      children={() => (
        <DistributionPieChart
          data={data}
          height={height}
          className={className}
          emptyStateIcon={Activity}
          emptyStateMessage="No operational risk data available"
          onSliceClick={handleSliceClick}
        />
      )}
      legends={() => (
        <>
          {data.map((item) => (
            <div key={item.name} className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm">{item.name}: {item.count}</span>
            </div>
          ))}
        </>
      )}
    />
  );
};

interface OverallRiskDistributionProps {
  data: DistributionData[];
  height?: number;
  className?: string;
  onNavigate?: (route: string, params?: any) => void;
  applicationId?: number;
  projectId?: number;
}

export const OverallRiskDistribution: React.FC<OverallRiskDistributionProps> = ({
  data,
  height = 192,
  className = '',
  onNavigate,
  applicationId,
  projectId
}) => {

  const handleSliceClick = (sliceName: string, count: number) => {
    if (!onNavigate || count === 0) return;

    const riskLevelMap: { [key: string]: string } = {
      'Critical': 'critical',
      'High': 'high',
      'Medium': 'medium',
      'Low': 'low'
    };

    const riskLevel = riskLevelMap[sliceName];

    const queryParams = new URLSearchParams();
    if (applicationId) queryParams.set('applicationId', applicationId.toString());
    if (projectId) queryParams.set('projectId', projectId.toString());

    queryParams.set('filter', 'overallscore:' + riskLevel);
    queryParams.set('columns', 'overallscore');
    onNavigate(`components-hub?${queryParams.toString()}`);
  };
  return (
    <ChartCard
      title="Overall Risk Distribution"
      description="Risk Distribution of components across all the Applications"
      icon={Activity}
      color="blue"
      hint="Breakdown of all components and dependencies by overall risk severity across applications."
      children={() => (
        <DistributionPieChart
          data={data}
          height={height}
          className={className}
          emptyStateIcon={Activity}
          emptyStateMessage="No overall risk data available"
          onSliceClick={handleSliceClick}
        />
      )}
      legends={() => (
        <>
          {data.map((item) => (
            <div key={item.name} className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm">{item.name}: {item.count}</span>
            </div>
          ))}
        </>
      )}
    />
  );
};

export default {
  VulnerabilityDistribution,
  LicenseRiskDistribution,
  OperationalRiskDistribution,
  OverallRiskDistribution
};